import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { Box, IconButton, ToggleButtonGroup } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Palette } from 'Palette';
import {
  SECONDARY_BUTTON_HOVER_SX,
  StyledButton,
  StyledToggleButton,
  TOGGLE_GROUP_HOVER_SX,
  TOGGLE_GROUP_SELECTED_BORDER_SX,
  TOGGLE_GROUP_WIDTH_SX,
} from '../../styles/StyledButtons';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading, StyledTypography } from '../../styles/styles';
import { ContentEditorToolbarProps, PageMode } from './newContentManagementEditorTypes';

const SAVE_STATUS_TONE: Record<string, string | undefined> = {
  error: Palette.danger.text,
  pending: Palette.warning.text,
  saving: Palette.warning.text,
  saved: Palette.default.disabledText,
};

export default function ContentEditorToolbar({
  fundOpportunity,
  titlePrefix = 'Fund Information',
  saveStatus,
  pageMode,
  toolbarRef,
  onBack,
  onPageModeChange,
  onSaveNow,
  onPublish,
  publishDisabled,
  published,
  onRevert,
  revertDisabled,
  reverting,
  onUploadPdf,
  pdfUploading,
  onPublishAll,
  publishAllDisabled,
  publishingAll,
  onRevertAll,
  revertAllDisabled,
  revertingAll,
}: ContentEditorToolbarProps) {
  // Switching View mode (e.g. Update Content -> Preview Web) can drastically
  // change the content height below this toolbar. If the admin was scrolled
  // down into the old content, the page then holds that same scroll offset
  // into whatever unrelated spot the new, shorter content now occupies —
  // scrolling back to the toolbar keeps the result where they can see it.
  // Tracked as a "previous value changed" comparison rather than an
  // isFirstRender ref with no cleanup — that pattern breaks under React 18
  // StrictMode's dev-only double-invoke, which fires this effect twice on
  // mount and would treat the second pass as a real page-mode change.
  const previousPageMode = useRef<PageMode | undefined>(undefined);
  useEffect(() => {
    if (previousPageMode.current !== undefined && previousPageMode.current !== pageMode) {
      toolbarRef.current?.scrollIntoView({ block: 'start' });
    }
    previousPageMode.current = pageMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMode]);

  return (
    <Box
      ref={toolbarRef}
      sx={{
        mt: 3,
        mb: 3,
        // The app's own header bar is position:fixed and sits on top of the
        // page (StyledAppBar, 72px/80px tall) — without this, scrollIntoView
        // lands this toolbar's top edge at the very top of the page, right
        // behind that fixed header, instead of just below it.
        scrollMarginTop: { xs: '72px', md: '80px' },
      }}
    >
      <StyledSection tone="tone1" noTopPadding>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={onBack}
            size="small"
            aria-label="Back"
            edge="start"
            sx={{ color: Palette.default.text }}
          >
            <BackIcon />
          </IconButton>
          <StyledHeading fullTopSpacing sx={{ mb: 0 }}>
            {titlePrefix}: {fundOpportunity?.name}
          </StyledHeading>
        </Box>

        {/* Row 1: primary actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
          {onSaveNow && (
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              sx={SECONDARY_BUTTON_HOVER_SX}
              startIcon={<SaveIcon />}
              onClick={onSaveNow}
              disabled={saveStatus === 'saving'}
            >
              Save
            </StyledButton>
          )}
          {onUploadPdf && (
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              sx={SECONDARY_BUTTON_HOVER_SX}
              startIcon={<PictureAsPdfIcon />}
              onClick={onUploadPdf}
              disabled={pdfUploading}
            >
              {pdfUploading ? 'Uploading PDF…' : 'Upload PDF'}
            </StyledButton>
          )}
          {onPublish && (
            <StyledButton
              noTopSpacing
              startIcon={published ? <CheckCircleIcon /> : <CloudUploadIcon />}
              onClick={onPublish}
              disabled={publishDisabled}
            >
              {published ? 'Published' : 'Publish'}
            </StyledButton>
          )}
          {onRevert && (
            <StyledButton
              noTopSpacing
              buttonVariant="warning"
              startIcon={<UndoIcon />}
              onClick={onRevert}
              disabled={revertDisabled}
            >
              {reverting ? 'Reverting…' : 'Revert to Published'}
            </StyledButton>
          )}
          {onPublishAll && (
            <StyledButton
              noTopSpacing
              startIcon={<CloudUploadIcon />}
              onClick={onPublishAll}
              disabled={publishAllDisabled}
            >
              {publishingAll ? 'Publishing All…' : 'Publish All'}
            </StyledButton>
          )}
          {onRevertAll && (
            <StyledButton
              noTopSpacing
              buttonVariant="warning"
              startIcon={<UndoIcon />}
              onClick={onRevertAll}
              disabled={revertAllDisabled}
            >
              {revertingAll ? 'Reverting…' : 'Revert To Published'}
            </StyledButton>
          )}
        </Box>

        {/* Save status, between the two button rows */}
        <StyledTypography
          sx={{
            display: 'block',
            mt: 1.5,
            mb: 4,
            fontSize: '0.75rem',
            color: SAVE_STATUS_TONE[saveStatus] ?? Palette.default.disabledText,
          }}
        >
          {saveStatus === 'saved' && 'All changes saved'}
          {saveStatus === 'pending' && 'Unsaved changes…'}
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'error' && 'Save failed'}
        </StyledTypography>

        {/* Row 2: view mode */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StyledHeading headerVariant="subheading" sx={{ m: 0, p: 0 }}>
            View
          </StyledHeading>
          <ToggleButtonGroup
            value={pageMode}
            exclusive
            onChange={(_, value) => value && onPageModeChange(value)}
            size="small"
          >
            <StyledToggleButton
              mini
              value="edit"
              sx={[TOGGLE_GROUP_HOVER_SX, TOGGLE_GROUP_SELECTED_BORDER_SX, TOGGLE_GROUP_WIDTH_SX]}
            >
              Update Content
            </StyledToggleButton>
            <StyledToggleButton
              mini
              value="preview-web"
              sx={[TOGGLE_GROUP_HOVER_SX, TOGGLE_GROUP_SELECTED_BORDER_SX, TOGGLE_GROUP_WIDTH_SX]}
            >
              Preview Web
            </StyledToggleButton>
            <StyledToggleButton
              mini
              value="preview-app"
              sx={[TOGGLE_GROUP_HOVER_SX, TOGGLE_GROUP_SELECTED_BORDER_SX, TOGGLE_GROUP_WIDTH_SX]}
            >
              Preview App
            </StyledToggleButton>
            <StyledToggleButton
              mini
              value="library"
              sx={[TOGGLE_GROUP_HOVER_SX, TOGGLE_GROUP_SELECTED_BORDER_SX, TOGGLE_GROUP_WIDTH_SX]}
            >
              Library
            </StyledToggleButton>
          </ToggleButtonGroup>
        </Box>
      </StyledSection>
    </Box>
  );
}
